import { useContext, useEffect, useState } from 'react';

import {
    Box,
    Card,
    CircularProgress,
    Typography,
} from "@mui/material";
import {
    addMonths,
    endOfMonth,
    endOfYear,
    isWithinInterval,
    startOfMonth
} from 'date-fns';
import { t } from 'i18next';
import api from '../../api';
import { setUser, updateUserWorkDays } from '../../context/actions';
import { Context } from '../../context/context';
import i18n from '../../i18n';
import { workLoadForDay } from '../../model/contractInfo';
import DateFormat from '../../utils/DateFormat';
import FlexTimeAccountBalance from '../shared/FlexTimeAccountBalance';
import NotificationCard from '../shared/NotificationCard';
import TimeTrackingForm from '../shared/TimeTrackingForm';
import ClosingDaysYearCard from './ClosingDaysYearCard';
import FlexTimeWizard from './FlexTimeWizard';
import UpcomingClosingDaysCard from './UpcomingClosingDaysCard';

export default function DashboardPage() {

    const [{ user }, dispatch] = useContext(Context);
    const [workDay, setWorkDay] = useState(null);
    const [prevEnd, setPrevEnd] = useState(null);
    const [nextStart, setNextStart] = useState(null);
    const [openWizard, setOpenWizard] = useState(false);
    const [currentFLextTimeAccount, setCurrentFlexTimeAccount] = useState(0);
    const [institute, setInstitute] = useState(null);
    const [location, setLocation] = useState(null);
    const [upcomingClosingDays, setUpcomingClosingDays] = useState(0);
    const [remainingClosingDays, setRemainingClosingDays] = useState(0);
    const [workHoursUpcomingClosingDays, setWorkHoursUpcomingClosingDays] = useState(0);
    const [workHoursRemainingClosingDays, setWorkHoursRemainingClosingDays] = useState(0);
    const [maxFlexTimeCredit, setMaxFlexTimeCredit] = useState(0);
    const [maxFlexTimeDeficit, setMaxFlexTimeDeficit] = useState(0);

    useEffect(() => {
        if (!institute || !location || !workDay || !user.contractInfo) return;

        if (institute.allowAdditionalCompensationForClosingDays) {
            // past closing days will not be considered
            // find upcoming closing days in current month
            const upcomingClosingDays = location.closingDays.filter(cd => {
                return isWithinInterval(cd, {
                    start: workDay.date,
                    end: endOfMonth(workDay.date),
                });
            });

            const neededWorkHours = upcomingClosingDays.reduce((partialSum, day) => partialSum + workLoadForDay(user.contractInfo, day.getDay()), 0);

            setWorkHoursUpcomingClosingDays(neededWorkHours);
            setUpcomingClosingDays(upcomingClosingDays.length);

            // remaining closing days this year, starting next month
            const closingDaysYear = location.closingDays
                .filter(cd => {
                    return isWithinInterval(cd, {
                        start: startOfMonth(addMonths(workDay.date, 1)),
                        end: endOfYear(workDay.date),
                    });
                });

            const sumAllClosingDays = closingDaysYear.reduce((partialSum, day) => partialSum + workLoadForDay(user.contractInfo, day.getDay()), 0);

            setRemainingClosingDays(closingDaysYear.length)
            setWorkHoursRemainingClosingDays(sumAllClosingDays);
            setMaxFlexTimeCredit(institute.maxFlexTimeCredit + sumAllClosingDays);
        } else {
            setMaxFlexTimeCredit(institute.maxFlexTimeCredit);
        };
        setMaxFlexTimeDeficit(institute.maxFlexTimeDeficit);
    }, [institute, location, workDay, user]);


    useEffect(() => {
        if (user.isAllowedFlexTimeAccount && user.hasFlexTimeAccount === null) {
            setOpenWizard(true);
        };

        if (user.id !== '') {
            if (user.contractInfo) {
                api.readCurrentFlexTimeAccount(user.id).then(duration => {
                    setCurrentFlexTimeAccount(duration)
                });
            };

            api.readInstitute(user.institute).then(res => {
                setInstitute(res);
            });

            setLocation(user.location);
        };

    }, [user]);

    useEffect(() => {

        if (user.id !== '') {
            const today = new Date();

            let wd = user.workDays.find(wd => {
                if (today.toISOString().split('T')[0] === wd.date.toISOString().split('T')[0]) {
                    return true;
                }
                return false;
            });

            if (!wd) {

                api.createWorkDay(user.id, { date: today, workEntries: [] }).then(res => {
                    api.readUser(user.id).then(res => {
                        dispatch(updateUserWorkDays(res.workDays));

                        let wd = res.workDays.find(wd => {
                            if (today.toISOString().split('T')[0] === wd.date.toISOString().split('T')[0]) {
                                return true;
                            };
                            return false;
                        });

                        let yesterday = new Date(today);
                        yesterday.setDate(today.getDate() - 1);

                        let prev = res.workDays.find(wd => {
                            if (yesterday.toISOString().split('T')[0] === wd.date.toISOString().split('T')[0]) {
                                return true;
                            };
                            return false;
                        });

                        let tomorrow = new Date(today);
                        tomorrow.setDate(today.getDate() + 1);

                        let next = res.workDays.find(wd => {
                            if (tomorrow.toISOString().split('T')[0] === wd.date.toISOString().split('T')[0]) {
                                return true;
                            }
                            return false;
                        });

                        setWorkDay(wd);
                        setPrevEnd(prev ? prev.end : null);
                        setNextStart(next ? next.start : null);
                    });
                });

            } else {
                let yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);

                let prev = user.workDays.find(wd => {
                    if (yesterday.toISOString().split('T')[0] === wd.date.toISOString().split('T')[0]) {
                        return true;
                    };
                    return false;
                });

                let tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                let next = user.workDays.find(wd => {
                    if (tomorrow.toISOString().split('T')[0] === wd.date.toISOString().split('T')[0]) {
                        return true;
                    };
                    return false;
                });

                setWorkDay(wd);
                setPrevEnd(prev ? prev.end : null);
                setNextStart(next ? next.start : null);
            };
        };
    }, [user.workDays]);

    const handleSaveWorkDay = (wd) => {
        api.updateWorkDay(user.id, wd.id, wd).then(res => {
            api.readUser(user.id).then(res => {
                dispatch(updateUserWorkDays(res.workDays));
            });
        });
    };

    const onCloseWizard = () => {
        const updatedUser = { ...user, hasFlexTimeAccount: false }
        api.updateUser(user.id, updatedUser).then(() => {
            dispatch(setUser(updatedUser));
        });
        setOpenWizard(false);
    };

    const onSubmitWizard = () => {
        const updatedUser = { ...user, hasFlexTimeAccount: true }
        api.updateUser(user.id, updatedUser).then(() => {
            dispatch(setUser(updatedUser));
        });
        setOpenWizard(false);
    };

    const onStartingWizard = () => {
        const updatedUser = { ...user, hasFlexTimeAccount: true }
        api.updateUser(user.id, updatedUser).then(() => {
            dispatch(setUser(updatedUser));
        });
    };

    return (
        <>
            <FlexTimeWizard
                open={openWizard}
                onClose={onCloseWizard}
                onStart={onStartingWizard}
                onSubmit={onSubmitWizard}
                userId={user.id}
            />
            <Box sx={{ display: 'flex', gap: 5, }}>
                <Box sx={{ flex: 0 }}>
                    <Card sx={{ p: 2, width: 'fit-content' }}>
                        {
                            !workDay &&
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress color="inherit" />
                            </Box>
                        }
                        {
                            workDay && <TimeTrackingForm
                                workDay={workDay}
                                onSave={handleSaveWorkDay}
                                withLink={true}
                                lastDayEnd={prevEnd}
                                nextDayStart={nextStart}
                            />
                        }
                    </Card>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {user.hasFlexTimeAccount &&
                        <>
                            <FlexTimeAccountBalance balance={currentFLextTimeAccount} />
                            {currentFLextTimeAccount > maxFlexTimeCredit &&
                                <NotificationCard
                                    title={t('hohesGuthaben', { ns: 'dashboardPage', defaultValue: 'Achtung: Hohes Zeitguthaben' })}
                                    variant={'warning'}
                                    text={
                                        // manual translation because of complexity
                                        i18n.language === 'de' ?
                                            <Typography>
                                                Du kannst maximal ein Guthaben von {DateFormat.formatDuration(maxFlexTimeCredit)} in den Folgemonat übernehmen, sofern keine Ausnahme abgesprochen ist.
                                                {workHoursRemainingClosingDays > 0 ?
                                                    <Typography sx={{ fontStyle: 'italic' }}>
                                                        {DateFormat.formatDuration(institute.maxFlexTimeCredit)} +
                                                        {DateFormat.formatDuration(workHoursRemainingClosingDays)} für die kommenden Brückentage diesen Jahres
                                                    </Typography>
                                                    :
                                                    ''
                                                }
                                            </Typography>
                                            :
                                            <Typography>
                                                You can carry over a maximum flexitime credit of {DateFormat.formatDuration(maxFlexTimeCredit)} into the following month, if not agreed upon otherwise.
                                                {workHoursRemainingClosingDays > 0 ?
                                                    <Typography>
                                                        {DateFormat.formatDuration(institute.maxFlexTimeCredit)} +
                                                        {DateFormat.formatDuration(workHoursRemainingClosingDays)} for remaining closing days this year
                                                    </Typography>
                                                    :
                                                    ''
                                                }
                                            </Typography>
                                    }
                                />
                            }
                            {currentFLextTimeAccount < maxFlexTimeDeficit &&
                                <NotificationCard
                                    title={t('hohesDefizit', { ns: 'dashboardPage', defaultValue: 'Achtung hohes Defizit' })}
                                    variant={'danger'}
                                    text={
                                        // manual translation because of complexity and consitancy
                                        i18n.language === 'de' ?
                                            <Typography>
                                                Du darfst maximal ein Defizit von {DateFormat.formatDuration(maxFlexTimeDeficit)} auf deinem Gleitzeitkonto haben, sofern keine Ausnahme abgesprochen ist.
                                            </Typography>
                                            :
                                            <Typography>
                                                Your flexitime account may not exeed a deficit of {DateFormat.formatDuration(maxFlexTimeDeficit)}, if not agreed upon otherwise.
                                            </Typography>
                                    }
                                />
                            }
                            {
                                upcomingClosingDays > 0 &&
                                <UpcomingClosingDaysCard
                                    date={workDay ? workDay.date : new Date()}
                                    flexTimeAccount={currentFLextTimeAccount}
                                    closingDays={upcomingClosingDays}
                                    workHoursClosingDays={workHoursUpcomingClosingDays}
                                />
                            }
                            {
                                remainingClosingDays > 0 &&
                                <ClosingDaysYearCard
                                    date={workDay ? workDay.date : new Date()}
                                    flexTimeAccount={currentFLextTimeAccount}
                                    closingDays={remainingClosingDays}
                                    workHoursRemainingClosingDays={workHoursRemainingClosingDays}
                                    workHoursClosingDays={workHoursUpcomingClosingDays}
                                />
                            }
                        </>
                    }
                </Box>
            </Box>
        </>
    );
};
